/**
 * Canonical permission catalogue for the whole platform.
 *
 * Permission strings follow the shape `<domain>.<action>` (or `platform.<domain>.<action>`
 * for SaaS-provider capabilities). They are seeded into the `permissions` table and referenced
 * from controllers via the `@Permissions(...)` decorator — never hard-coded as bare strings.
 */
export const PERMISSIONS = {
  // Platform administration — SaaS provider staff only, never granted to tenant users.
  PLATFORM_TENANTS_VIEW: 'platform.tenants.view',
  PLATFORM_TENANTS_CREATE: 'platform.tenants.create',
  PLATFORM_TENANTS_UPDATE: 'platform.tenants.update',
  PLATFORM_TENANTS_SUSPEND: 'platform.tenants.suspend',
  PLATFORM_USERS_VIEW: 'platform.users.view',
  PLATFORM_AUDIT_VIEW: 'platform.audit.view',

  // Tenant settings and profile.
  TENANT_VIEW: 'tenant.view',
  TENANT_UPDATE: 'tenant.update',

  // Branches.
  BRANCHES_VIEW: 'branches.view',
  BRANCHES_CREATE: 'branches.create',
  BRANCHES_UPDATE: 'branches.update',
  BRANCHES_DELETE: 'branches.delete',

  // Identity: users, roles and access assignment inside a tenant.
  USERS_VIEW: 'users.view',
  USERS_CREATE: 'users.create',
  USERS_UPDATE: 'users.update',
  USERS_DELETE: 'users.delete',
  ROLES_VIEW: 'roles.view',
  ROLES_MANAGE: 'roles.manage',

  // Audit trail.
  AUDIT_VIEW: 'audit.view',

  // ----- Business domains below are declared now and implemented in later phases. -----

  CUSTOMERS_VIEW: 'customers.view',
  CUSTOMERS_CREATE: 'customers.create',
  CUSTOMERS_EDIT: 'customers.edit',
  // Customers are deactivated, never deleted, so there is no `customers.delete`.
  CUSTOMERS_CHANGE_STATUS: 'customers.change_status',

  CATALOG_VIEW: 'catalog.view',
  CATALOG_CREATE: 'catalog.create',
  CATALOG_EDIT: 'catalog.edit',
  // Products are discontinued, never deleted, so there is no `catalog.delete`.
  CATALOG_CHANGE_STATUS: 'catalog.change_status',

  INVENTORY_VIEW: 'inventory.view',
  INVENTORY_RECEIVE: 'inventory.receive',
  // Correcting a balance is a higher-trust action than booking in a delivery.
  INVENTORY_ADJUST: 'inventory.adjust',
  INVENTORY_TRANSFER: 'inventory.transfer',

  SALES_VIEW: 'sales.view',
  SALES_CREATE: 'sales.create',
  SALES_REFUND: 'sales.refund',
  SALES_DISCOUNT: 'sales.discount',
  /** Approve new credit exposure past a customer's limit. Requires a recorded reason. */
  SALES_CREDIT_OVERRIDE: 'sales.credit_override',

  PAYMENTS_VIEW: 'payments.view',
  PAYMENTS_CREATE: 'payments.create',
  /** Direct a payment at named instalments instead of the default oldest-due-first walk. */
  PAYMENTS_ALLOCATE: 'payments.allocate',

  MEDICAL_VIEW: 'medical.view',
  MEDICAL_CREATE: 'medical.create',
  MEDICAL_UPDATE: 'medical.update',
  /** Sign an eye examination off as its examiner. Holding it is also what makes someone eligible to be named examiner. */
  MEDICAL_COMPLETE: 'medical.complete',
  /** Void a completed eye examination. The record and its prescription stay, marked voided. */
  MEDICAL_VOID: 'medical.void',

  MEASUREMENTS_VIEW: 'measurements.view',
  MEASUREMENTS_CREATE: 'measurements.create',

  // Recording and voiding a customer's face shape. Styling information, not clinical, so it is granted broadly.
  FACE_ASSESS: 'face.assess',

  // Ranking catalogue frames for a customer. Grants no stock visibility and no customer access of its own.
  FRAME_RECOMMEND: 'frame.recommend',

  // Optical jobs: the spectacles on an order, from dispensing to collection. None of these grants a medical permission; reading a job's dispensing prescription is not reading the clinical record.
  /** Read optical jobs and the dispensing prescription each carries. */
  OPTICAL_VIEW: 'optical.view',
  /** Create optical jobs when an order is confirmed. */
  OPTICAL_CREATE: 'optical.create',
  /** Correct a job's details while it has not gone to the lab. */
  OPTICAL_UPDATE: 'optical.update',
  /** Move a job through the lab: sent, in the lab, received, ready. */
  OPTICAL_LAB_WORKFLOW: 'optical.lab_workflow',
  /** Hand a finished job to the customer. */
  OPTICAL_DELIVER: 'optical.deliver',
  /** Cancel an optical order before it goes to the lab. Refunding money on it also needs sales.refund. */
  OPTICAL_CANCEL: 'optical.cancel',
  /** Keep the tenant's list of labs. */
  OPTICAL_MANAGE_LABS: 'optical.manage_labs',

  SUPPLIERS_VIEW: 'suppliers.view',
  SUPPLIERS_MANAGE: 'suppliers.manage',

  PURCHASING_VIEW: 'purchasing.view',
  PURCHASING_CREATE: 'purchasing.create',
  /** Edit a draft purchase order, or an open order's expected date, supplier reference and notes. */
  PURCHASING_UPDATE: 'purchasing.update',
  /** Place a purchase order with the supplier: the commitment itself. */
  PURCHASING_APPROVE: 'purchasing.approve',
  /** Post a goods receipt against a purchase order at its branch. Needs no inventory permission. */
  PURCHASING_RECEIVE: 'purchasing.receive',
  /** Cancel a purchase order that received nothing, or close the remainder of a partly received one. */
  PURCHASING_CANCEL: 'purchasing.cancel',
  /** Post and void supplier invoices. */
  PURCHASING_INVOICE: 'purchasing.invoice',

  /** Read what is owed to suppliers: outstanding, ageing and the payments already made. */
  PAYABLES_VIEW: 'payables.view',
  /** Record a supplier payment and void one — the same capability, as voiding is how a mis-entered payment is corrected. */
  PAYABLES_PAY: 'payables.pay',

  /** Read operating expenses and the category master that classifies them. */
  EXPENSES_VIEW: 'expenses.view',
  /** Record a direct-paid operating expense, and void one — the same capability, as voiding is how a mis-entered expense is corrected. */
  EXPENSES_MANAGE: 'expenses.manage',
  /** Maintain the tenant-wide expense category master. Separate from recording an expense: classifying the business is not the same job as paying its bills. */
  EXPENSES_CATEGORIES_MANAGE: 'expenses.categories.manage',
  /** @deprecated Inert compatibility key from before Phase 4.4. No runtime route authorises with it; existing grants are kept so no tenant loses a row. */
  EXPENSES_CREATE: 'expenses.create',
  /** @deprecated Inert compatibility key from before Phase 4.4. Expenses are direct-paid and have no approval step; no runtime route authorises with it. */
  EXPENSES_APPROVE: 'expenses.approve',

  ATTENDANCE_VIEW: 'attendance.view',
  ATTENDANCE_MANAGE: 'attendance.manage',

  // CRM (Phase 5.3). Reading a customer's messaging history and preparing a message are separate
  // from maintaining the tenant's template master, which is a job for whoever owns the wording.
  NOTIFICATIONS_VIEW: 'notifications.view',
  NOTIFICATIONS_SEND: 'notifications.send',
  /** Maintain the tenant-wide bilingual template master. Deciding what every branch says to every customer is not the same job as messaging one of them. */
  NOTIFICATIONS_TEMPLATES_MANAGE: 'notifications.templates.manage',

  AUTOMATIONS_VIEW: 'automations.view',
  AUTOMATIONS_MANAGE: 'automations.manage',

  WHOLESALE_VIEW: 'wholesale.view',
  WHOLESALE_MANAGE: 'wholesale.manage',

  REPORTING_VIEW: 'reporting.view',
  REPORTING_EXPORT: 'reporting.export',
} as const;

export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/** Grants every permission. Reserved for the platform super admin role. */
export const WILDCARD_PERMISSION = '*';

/** A grant stored against a role: either a concrete permission, `domain.*`, or `*`. */
export type PermissionGrant = Permission | typeof WILDCARD_PERMISSION | `${string}.*`;

export const PERMISSION_LIST: readonly Permission[] = Object.values(PERMISSIONS);

/** Human-readable labels used when seeding the permission catalogue and in admin UIs. */
export const PERMISSION_DOMAINS = {
  platform: 'Platform administration',
  tenant: 'Tenant settings',
  branches: 'Branches',
  users: 'Users',
  roles: 'Roles and permissions',
  audit: 'Audit trail',
  customers: 'Customers',
  catalog: 'Product catalog',
  inventory: 'Inventory',
  sales: 'Sales',
  payments: 'Payments',
  medical: 'Medical records',
  measurements: 'Optical measurements',
  face: 'Face shape',
  frame: 'Frame recommendations',
  optical: 'Optical orders',
  suppliers: 'Suppliers',
  purchasing: 'Purchasing',
  payables: 'Payables',
  expenses: 'Expenses',
  attendance: 'Attendance',
  notifications: 'Notifications',
  automations: 'Automations',
  wholesale: 'Wholesale',
  reporting: 'Reporting',
} as const;

export type PermissionDomain = keyof typeof PERMISSION_DOMAINS;

/** Returns the leading domain segment of a permission string, e.g. `customers.view` -> `customers`. */
export function permissionDomain(permission: string): string {
  return permission.split('.')[0] ?? permission;
}
